import { itemField, record, upper, type Row } from '../ability/context.js';
import { readInventoryField, readInventoryRows, readLayerListEntryUuid } from '../compile/inventory-raw-readers.js';
import { RUBBLE_SPAWN_TABLE } from './rubble-spawn-table.js';
import { normalizePhpListish } from '../../shared/php-listish.js';
import { fullDefinition } from '../reactions/php-row-rules.js';
import { waypointMarkerFromHints } from './destroy-object.js';
import { payloadDefinitionIds } from './destruction-payload.js';

/** Dependencies of object damage, including the branches which only a killing hit reaches.
 * Inventory arrivals warm the spawn table through the same adoption path.
 * This discovers inputs only; the destruction planner still decides rubble, spill and damage. */
export async function prepareInventoryDamageDefinitions(sources: readonly Row[],
  read: { definition: (id: string) => Row | null; entries: (row: Row, key: string) => unknown[] },
  fetchDefinitions: (ids: string[]) => Promise<void>): Promise<void> {
  const rows: Row[] = [], seen = new Set<Row>();
  const walk = (source: Row): void => {
    if (seen.has(source)) return;
    seen.add(source);
    if (readInventoryField(source, 'inventory_id')) rows.push(source);
    for (const key of ['encounter_inventories_arr', 'inventory', 'inventory_in_container'] as const)
      for (const child of readInventoryRows(source, key)) walk(child);
    for (const child of normalizePhpListish(source.encounter_creatures_arr)) {
      const row = record(child); if (row) walk(row);
    }
  };
  sources.forEach(walk);
  if (!rows.length) return;
  const wanted = new Set<string>(RUBBLE_SPAWN_TABLE.flatMap(entry => entry.anchor ? [entry.anchor.itemId] : []));
  for (const row of rows) wanted.add(upper(readInventoryField(row, 'inventory_item_id')));
  const asked = new Set<string>();
  const payloadIds = new Set<string>();
  for (;;) {
    const heldCount = wanted.size;
    // Row overrides and template composition both use the production layer reader.
    for (const source of [...rows, ...[...wanted].map(read.definition).filter((r): r is Row => !!r)]) {
      const template = read.definition(upper(readInventoryField(source, 'inventory_item_id')));
      const target = template ? { ...source, item_layer_types: { ...record(template.item_layer_types), ...record(source.item_layer_types) } } : source;
      const name = String(itemField(template ?? source, 'item_name') ?? '');
      if (itemField(template ?? source, 'item_type') === 'door' || /^Rubble,\s/.test(name))
        wanted.add(waypointMarkerFromHints(String(readInventoryField(source, 'inventory_name') ?? ''), name));
      for (const entry of read.entries(target, 'destruction_effect_ability')) {
        const id = readLayerListEntryUuid(entry);
        if (id) payloadIds.add(id);
      }
      if (payloadIds.has(upper(itemField(target, 'item_id'))))
        for (const id of payloadDefinitionIds(target)) wanted.add(id);
      for (const entry of [...read.entries(target, 'item_composition'), ...read.entries(target, 'destruction_effect_ability')]) {
        const id = readLayerListEntryUuid(entry);
        if (!id) continue;
        wanted.add(id);
      }
    }
    const missing = [...wanted].filter(id => /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)
      && !asked.has(id) && !fullDefinition(read.definition(id), 'any'));
    if (!missing.length) {
      if (wanted.size === heldCount) return;
      continue;
    }
    missing.forEach(id => asked.add(id));
    await fetchDefinitions(missing);
    // Missing/forbidden ids are never retried in this pass or certified as held.
    // The resolver retains its exact named pre-draw refusal for the affected object.
  }
}
